import { useEffect, useState } from "react";
import { Box, Container, Typography } from "@mui/material";
import { useLocation } from "react-router";
import { PersonalityQuiz } from "../../components/personality-quiz";
import { PersonalityQuizFormGateModal } from "../../components/personality-quiz-form-gate-modal";

const setMetaTag = (attrName: "name" | "property", attrValue: string, content: string) => {
  const selector = `meta[${attrName}="${attrValue}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  const previousContent = existing?.getAttribute("content");
  const tag = existing ?? document.createElement("meta");

  if (!existing) {
    tag.setAttribute(attrName, attrValue);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);

  return () => {
    if (!existing) {
      tag.remove();
      return;
    }
    if (previousContent === null || typeof previousContent === "undefined") {
      tag.remove();
      return;
    }
    tag.setAttribute("content", previousContent);
  };
};

const PersonalityQuizPage = () => {
  const location = useLocation();
  const [leadCaptureCompleted, setLeadCaptureCompleted] = useState(false);

  useEffect(() => {
    const pageTitle = "Discover Your Designer Personality | Home Design Guild";
    const description =
      "Take the Home Design Guild personality quiz to discover your designer style, strengths, and how you collaborate on projects.";
    const previousTitle = document.title;
    const pageUrl = typeof window !== "undefined" ? window.location.href : "";

    document.title = pageTitle;

    const cleanupFns = [
      setMetaTag("name", "description", description),
      setMetaTag("property", "og:title", pageTitle),
      setMetaTag("property", "og:description", description),
      setMetaTag("property", "og:type", "website"),
      ...(pageUrl ? [setMetaTag("property", "og:url", pageUrl)] : []),
      setMetaTag("name", "twitter:card", "summary"),
      setMetaTag("name", "twitter:title", pageTitle),
      setMetaTag("name", "twitter:description", description),
    ];

    return () => {
      document.title = previousTitle;
      cleanupFns.forEach((cleanup) => cleanup());
    };
  }, [location.pathname]);

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        minHeight: "100vh",
        py: { xs: 6, md: 10 },
      }}
    >
      {!leadCaptureCompleted ? (
        <Container maxWidth="md">
          <Box textAlign="center" mb={{ xs: 4, md: 6 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#2D3B53",
                mb: 0,
                maxWidth: 720,
                mx: "auto",
                fontSize: { xs: "1.1rem", md: "1.5rem" },
                lineHeight: 1.6,
              }}
            >
              Complete this brief form to proceed to the personality quiz to discover
              your personality type and collaboration style.
            </Typography>
          </Box>
        </Container>
      ) : null}

      {!leadCaptureCompleted ? (
        <Container maxWidth="md">
          <Box sx={{ minHeight: "1195px" }}>
            <PersonalityQuizFormGateModal
              open
              onCompleted={() => setLeadCaptureCompleted(true)}
            />
          </Box>
        </Container>
      ) : (
        <PersonalityQuiz
          showSuperPowerCard
          contentVariant="designer"
          beginLabel="Start Quiz"
        />
      )}
    </Box>
  );
};

export default PersonalityQuizPage;
